<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { ArrowRight, Check, Download, Layers3, RotateCcw, Share2, Sparkles } from '@lucide/vue'
import { api } from '../api'
import type { ResultData } from '../types'

const route = useRoute()
const result = ref<ResultData | null>(null)
const shared = ref(false)

onMounted(async () => {
  const token = typeof route.query.token === 'string' ? `?token=${encodeURIComponent(route.query.token)}` : ''
  result.value = await api(`/api/results/${route.params.id}${token}`)
  if (result.value) {
    const shareTitle = result.value.profile ? `我在心光量域得到「${result.value.profile.title}」` : `我完成了《${result.value.surveyTitle}》`
    document.title = shareTitle
    for (const [property, content] of [['og:title', shareTitle], ['og:description', '一份用于自我探索的游戏化问卷结果'], ['og:url', location.href]] as const) {
      let meta = document.head.querySelector<HTMLMetaElement>(`meta[property="${property}"]`)
      if (!meta) { meta = document.createElement('meta'); meta.setAttribute('property', property); document.head.appendChild(meta) }
      meta.content = content
    }
  }
  requestAnimationFrame(() => {
    document.querySelectorAll<HTMLElement>('.confetti-piece').forEach((piece, index) => {
      piece.animate(
        [{ opacity: 0, transform: 'translateY(-20px) rotate(0deg)' }, { opacity: 1, offset: 0.15 }, { opacity: 0, transform: 'translateY(260px) rotate(220deg)' }],
        { delay: index * 25, duration: 1800, easing: 'ease-out', fill: 'both' },
      )
    })
  })
})

const radarPoints = computed(() => {
  if (!result.value) return ''
  const dimensions = result.value.dimensions
  const center = 100
  return dimensions.map((dimension, index) => {
    const angle = (-Math.PI / 2) + (index / dimensions.length) * Math.PI * 2
    const radius = 72 * ((result.value?.scores[dimension.key] ?? 50) / 100)
    return `${center + Math.cos(angle) * radius},${center + Math.sin(angle) * radius}`
  }).join(' ')
})

const axes = computed(() => {
  if (!result.value) return []
  return result.value.dimensions.map((dimension, index) => {
    const angle = (-Math.PI / 2) + (index / result.value!.dimensions.length) * Math.PI * 2
    return { ...dimension, x: 100 + Math.cos(angle) * 85, y: 100 + Math.sin(angle) * 85 }
  })
})

async function share() {
  if (!result.value) return
  const text = result.value.profile
    ? `我在心光量域完成了「${result.value.surveyTitle}」，得到 ${result.value.profile.title}：${result.value.profile.tags.join('、')}`
    : `我在心光量域完成了「${result.value.surveyTitle}」，查看了自己的维度结果。`
  if (navigator.share) await navigator.share({ title: '我的心光画像', text, url: location.href })
  else await navigator.clipboard.writeText(`${text}\n${location.href}`)
  shared.value = true
  setTimeout(() => shared.value = false, 1800)
}

async function downloadShareCard() {
  if (!result.value) return
  const canvas = document.createElement('canvas')
  canvas.width = 1200; canvas.height = 630
  const context = canvas.getContext('2d')
  if (!context) return
  const accent = result.value.profile?.color || result.value.accent
  const gradient = context.createLinearGradient(0, 0, 1200, 630)
  gradient.addColorStop(0, '#f6edda'); gradient.addColorStop(1, /^#[0-9a-f]{6}$/i.test(accent) ? accent : '#718064')
  context.fillStyle = gradient; context.fillRect(0, 0, canvas.width, canvas.height)
  context.fillStyle = '#2f2923'; context.font = '36px serif'; context.fillText('心光量域 · LumiScale', 72, 90)
  context.font = '28px serif'; context.fillText(result.value.surveyTitle, 72, 160)
  context.font = 'bold 72px serif'; context.fillText(result.value.profile?.title || '维度结果概览', 72, 290)
  context.font = '28px serif'; context.fillText(result.value.profile?.tags.join('  ·  ') || '仅展示本次作答的维度分数', 72, 360)
  context.font = '22px serif'; context.fillText('这是一份用于自我探索的娱乐性结果，不构成医学诊断。', 72, 540)
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'))
  if (!blob) return
  const file = new File([blob], '心光量域-结果卡片.png', { type: 'image/png' })
  if (navigator.share && navigator.canShare?.({ files: [file] })) {
    await navigator.share({ title: '我的心光量域结果', files: [file] })
    return
  }
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a'); link.href = url; link.download = file.name; link.click(); URL.revokeObjectURL(url)
  shared.value = true
  setTimeout(() => shared.value = false, 1800)
}
</script>

<template>
  <div v-if="!result" class="page-loading"><span class="loader-orb"/><p>正在把答案折成一束光…</p></div>
  <div v-else class="result-page section-pad" :style="{ '--profile': result.profile?.color || result.accent }">
    <div v-if="result.crisisTriggered" class="security-notice"><Check/><p>你已看到安全支持提示。若不适感仍在持续，请优先联系可信任的人或当地专业支持。</p></div>
    <div class="confetti-field" aria-hidden="true"><i v-for="n in 24" :key="n" class="confetti-piece" :style="{ '--i': n }"/></div>
    <header class="result-header">
      <p class="eyebrow"><Sparkles :size="14"/>{{ result.resultMode === 'scores_only' ? 'YOUR DIMENSION MAP' : 'YOUR LUMI PROFILE' }}</p>
      <span>问卷版本 V{{ result.version }} · 实验界面 {{ result.variant }}</span>
    </header>

    <section v-if="result.profile" class="profile-hero">
      <div class="profile-avatar">
        <span class="avatar-ring ring-a"/><span class="avatar-ring ring-b"/>
        <div class="avatar-core"><img v-if="result.profile.imageUrl" :src="result.profile.imageUrl" :alt="`${result.profile.title}角色图`"/><b v-else>{{ result.profile.emoji }}</b><i/></div>
      </div>
      <div class="profile-copy">
        <p>{{ result.surveyTitle }} 为你映出的角色是</p>
        <h1>{{ result.profile.title }}</h1>
        <div class="tag-row"><span v-for="tag in result.profile.tags" :key="tag">{{ tag }}</span></div>
        <blockquote>{{ result.profile.description }}</blockquote>
      </div>
    </section>
    <section v-else class="scores-only-hero">
      <div><span>无 IP 角色</span><Layers3/></div>
      <div><p>{{ result.surveyTitle }} 为你生成了</p><h1>维度结果概览</h1><blockquote>这份结果只呈现你的作答分数，不把你归入任何角色或人格标签。</blockquote></div>
    </section>

    <section class="result-grid">
      <article class="radar-card">
        <div class="card-title"><span><small>01</small><h2>你的能量图谱</h2></span><p>不是排名，只是此刻的你</p></div>
        <div class="radar-wrap">
          <svg viewBox="0 0 200 200" aria-label="维度雷达图">
            <circle cx="100" cy="100" r="72" class="radar-grid"/><circle cx="100" cy="100" r="48" class="radar-grid"/><circle cx="100" cy="100" r="24" class="radar-grid"/>
            <line v-for="axis in axes" :key="axis.key" x1="100" y1="100" :x2="axis.x" :y2="axis.y" class="radar-axis"/>
            <polygon :points="radarPoints" class="radar-shape"/>
            <circle v-for="axis in axes" :key="`p-${axis.key}`" :cx="100 + (axis.x - 100) * ((result.scores[axis.key] || 50) / 100) * 0.85" :cy="100 + (axis.y - 100) * ((result.scores[axis.key] || 50) / 100) * 0.85" r="3.5" class="radar-point"/>
          </svg>
          <div class="radar-labels">
            <span v-for="dimension in result.dimensions" :key="dimension.key"><i :style="{ background: dimension.color }"/>{{ dimension.label }}<b>{{ result.scores[dimension.key] }}</b></span>
          </div>
        </div>
      </article>

      <article class="advice-card">
        <div class="card-title"><span><small>02</small><h2>{{ result.profile ? '今天的成长线索' : '完成说明' }}</h2></span></div>
        <div class="advice-symbol">{{ result.profile ? '↗' : '✓' }}</div>
        <p v-if="result.profile">{{ result.profile.advice }}</p>
        <p v-else>分数反映本次自陈作答，不代表固定人格、能力高低或医学结论。你可以结合具体情境理解各维度。</p>
        <div class="completion-note"><Check/>{{ result.completionMessage }}</div>
      </article>
    </section>

    <section class="result-actions">
      <button class="button button-ghost" @click="downloadShareCard"><Download/>生成结果卡片</button>
      <button class="button button-dark" @click="share"><Check v-if="shared"/><Share2 v-else/>{{ shared ? '已复制分享内容' : result.profile ? '分享我的画像' : '分享维度结果' }}</button>
      <RouterLink class="button button-ghost" :to="`/surveys/${result.slug}`"><RotateCcw/>再看一次问卷</RouterLink>
      <RouterLink class="text-link" to="/explore">继续探索 <ArrowRight/></RouterLink>
    </section>

    <p class="result-disclaimer">本结果根据你的自陈回答生成，用于自我探索，不构成医学诊断、治疗建议或能力评价。问卷内容与角色素材由创作者提供；平台保留审核、限制展示、下架和处理投诉的权利。</p>
  </div>
</template>
